import { useId } from "react";
import { useModalSurface } from "../primitives/useModalSurface";

export interface ImageZoomOverlayProps {
  readonly src: string;
  readonly alt: string;
  /**
   * The figure's caption, as it reads beneath the image in the document.
   *
   * A CloudoX diagram caption is not decoration: it carries the decision
   * question, the evidence basis, how many elements of the total are shown, and
   * what was omitted. Enlarging the picture without it hands the reader the one
   * view of the figure that makes no claim about its completeness — which is the
   * view most likely to be screenshotted into a document.
   */
  readonly caption?: string;
  readonly onClose: () => void;
}

/**
 * Full-screen image lightbox, for reading a diagram that is too small in the
 * flow of the document.
 *
 * It is a modal dialog and behaves like one: focus moves into it, Tab cycles
 * within it, Escape and the close button dismiss it, and focus returns to the
 * image that opened it. Previously it did none of those — a keyboard user who
 * enlarged a diagram was left tabbing through a document they could no longer
 * see, with no way back to where they had been reading.
 */
export function ImageZoomOverlay({
  src,
  alt,
  caption,
  onClose,
}: ImageZoomOverlayProps) {
  const captionId = useId();
  const panelRef = useModalSurface({ onClose });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2.5vmin",
        backgroundColor:
          "color-mix(in oklab, var(--color-ink-950, #0b1020) 86%, transparent)",
        backdropFilter: "blur(2px)",
      }}
    >
      {/* The backdrop is its own element so that clicking the figure — the
          thing the reader came here to look at — does not dismiss it. */}
      <div
        style={{ position: "absolute", inset: 0, cursor: "zoom-out" }}
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={alt ? `Enlarged image: ${alt}` : "Enlarged image"}
        {...(caption ? { "aria-describedby": captionId } : {})}
        tabIndex={-1}
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.75rem",
          maxWidth: "96vw",
          maxHeight: "94vh",
        }}
      >
        <button
          type="button"
          aria-label="Close enlarged image"
          onClick={onClose}
          style={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "2.25rem",
            height: "2.25rem",
            borderRadius: "9999px",
            border: "1px solid rgba(255,255,255,0.25)",
            background: "rgba(255,255,255,0.12)",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        <img
          src={src}
          alt={alt}
          style={{
            maxWidth: "96vw",
            maxHeight: caption ? "76vh" : "90vh",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            borderRadius: "0.5rem",
            boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
            background: "#fff",
          }}
        />
        {caption ? (
          <p
            id={captionId}
            style={{
              maxWidth: "min(80ch, 92vw)",
              maxHeight: "14vh",
              overflowY: "auto",
              margin: 0,
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              textAlign: "center",
              color: "rgba(255,255,255,0.88)",
            }}
          >
            {caption}
          </p>
        ) : null}
      </div>
    </div>
  );
}
